
import { useState, useEffect, createContext, useContext, ReactNode } from 'react';

export interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  ccpaOptOut: boolean;
}

interface CookieConsentContextType {
  hasConsented: boolean;
  preferences: CookiePreferences;
  showBanner: boolean;
  showSettings: boolean;
  acceptAll: () => void;
  rejectAll: () => void;
  savePreferences: (prefs: CookiePreferences) => void;
  openSettings: () => void;
  closeSettings: () => void;
  closeBanner: () => void;
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined);

const defaultPreferences: CookiePreferences = {
  essential: true,
  analytics: false,
  marketing: false,
  ccpaOptOut: false,
};

export const CookieConsentProvider = ({ children }: { children: ReactNode }) => {
  const [hasConsented, setHasConsented] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('docketlabs-cookie-consent');
    const savedPrefs = localStorage.getItem('docketlabs-cookie-preferences');
    
    if (consent === 'true') {
      setHasConsented(true);
      if (savedPrefs) {
        setPreferences(JSON.parse(savedPrefs));
      }
    } else {
      setShowBanner(true);
    }
  }, []);

  const acceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      marketing: true,
      ccpaOptOut: false,
    };
    setPreferences(allAccepted);
    setHasConsented(true);
    setShowBanner(false);
    localStorage.setItem('docketlabs-cookie-consent', 'true');
    localStorage.setItem('docketlabs-cookie-preferences', JSON.stringify(allAccepted));
  };

  const rejectAll = () => {
    const onlyEssential: CookiePreferences = {
      essential: true,
      analytics: false,
      marketing: false,
      ccpaOptOut: true,
    };
    setPreferences(onlyEssential);
    setHasConsented(true);
    setShowBanner(false);
    localStorage.setItem('docketlabs-cookie-consent', 'true');
    localStorage.setItem('docketlabs-cookie-preferences', JSON.stringify(onlyEssential));
  };

  const savePreferences = (prefs: CookiePreferences) => {
    setPreferences(prefs);
    setHasConsented(true);
    setShowBanner(false);
    setShowSettings(false);
    localStorage.setItem('docketlabs-cookie-consent', 'true');
    localStorage.setItem('docketlabs-cookie-preferences', JSON.stringify(prefs));
  };

  const openSettings = () => setShowSettings(true);
  const closeSettings = () => setShowSettings(false);
  const closeBanner = () => setShowBanner(false);

  return (
    <CookieConsentContext.Provider value={{
      hasConsented,
      preferences,
      showBanner,
      showSettings,
      acceptAll,
      rejectAll,
      savePreferences,
      openSettings,
      closeSettings,
      closeBanner,
    }}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (context === undefined) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return context;
};
